import React, { useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';

import Cookie from '../../svg/Cookie.svg';
import Headline from '../atoms/Headline';
import Copy from '../atoms/Copy';
import Button from '../atoms/Button';
import { Link } from 'react-router-dom';

const StyledBackdrop = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  z-index: 3;
  background-color: rgba(255, 237, 115, 0.5);
`;

const StyledBanner = styled.div`
  position: absolute;
  right: 50%;
  bottom: 0;
  transform: translateX(50%);
  width: 100%;
  max-width: var(--max-width-m);
`;

const StyledCookie = styled(Cookie)`
  position: absolute;
  top: 0;
  left: 25px;
  transform: translateY(-50%);
  width: var(--cookie-width);
  height: var(--cookie-width);
`;

const StyledInner = styled.div`
  padding: var(--distance-3xl) var(--distance-l) var(--distance-xl);
  border-radius: var(--border-radius-s) var(--border-radius-s) 0 0;
  background-color: var(--color-brand-primary);
`;

const StyledHeadline = styled(Headline)`
  color: var(--color-accent-secondary);
  font-size: var(--font-size-s);
  line-height: var(--line-height-3xs);
  font-family: var(--font-family-graphik);
  text-transform: unset;
`;

const StyledCopy = styled(Copy)`
  color: var(--color-accent-secondary);
  font-size: var(--font-size-xxs);
  line-height: var(--line-height-4xs);

  a {
    color: var(--color-accent-secondary);
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--distance-xxs);
  margin: 0 var(--distance-4xs);
  padding: 0 var(--distance-l);
`;

const StyledPrimaryButton = styled(Button)`
  display: flex;
  justify-content: center;
  border: 2px solid var(--color-accent-secondary);
  padding: var(--distance-4xs) var(--distance-s);
  width: 100%;
  font-family: var(--font-family-graphik);
  background-color: var(--color-accent-secondary);
  color: var(--color-brand-primary);

  &:hover {
    border: 2px solid var(--color-accent-secondary);
    background-color: var(--color-accent-primary);
    color: var(--color-accent-secondary);
  }

  &:focus {
    border: 2px solid var(--color-accent-secondary);
    background-color: var(--color-accent-primary);
    color: var(--color-accent-secondary);
  }
`;

const StyledSecondaryButton = styled(Button)`
  display: flex;
  justify-content: center;
  border: 2px solid var(--color-accent-secondary);
  padding: var(--distance-4xs) 25px;
  width: 100%;
  font-family: var(--font-family-graphik);
  background-color: var(--color-brand-primary);
  color: var(--color-accent-secondary);

  &:hover {
    border: 2px solid var(--color-accent-secondary);
    background-color: var(--color-accent-secondary);
    color: var(--color-accent-primary);
  }

  &:focus {
    border: 2px solid var(--color-accent-secondary);
    background-color: var(--color-accent-secondary);
    color: var(--color-accent-primary);
  }
`;

const CookieBanner = () => {
  const { t, i18n } = useTranslation();
  const [cookies, setCookie] = useCookies(['cookieConsent']);

  const allowCookies = () => {
    setCookie('cookieConsent', true);
  };

  const denyCookies = () => {
    setCookie('cookieConsent', false);
  };

  return cookies.cookieConsent === undefined ? (
    <StyledBackdrop>
      <StyledBanner>
        <StyledCookie />
        <StyledInner>
          <StyledHeadline label={t('cookie.headline') as string} />
          <StyledCopy>
            <Trans t={t} i18nKey={'cookie.copy'}>
              Wenn du auf „Alle Cookies akzeptieren“ klickst, stimmst du der Speicherung von Cookies
              auf deinem Gerät zu, um die Website-Navigation zu verbessern, die Website-Nutzung zu
              analysieren und unser Marketing zu unterstützen.{' '}
              <Link to="/datenschutz?access=1">Datenschutzerklärung</Link>
            </Trans>
          </StyledCopy>
          <StyledButtonContainer>
            <StyledPrimaryButton
              theme={'secondary'}
              buttonType="button"
              label={t('cookie.button_label_1') as string}
              onClick={() => {
                allowCookies();
              }}
            />
            <StyledSecondaryButton
              theme={'secondary'}
              buttonType="button"
              label={t('cookie.button_label_2') as string}
              onClick={() => {
                denyCookies();
              }}
            />
          </StyledButtonContainer>
        </StyledInner>
      </StyledBanner>
    </StyledBackdrop>
  ) : null;
};
export default CookieBanner;
